import { useState, useEffect, useCallback, useMemo, useRef, type ReactNode } from 'react';
import { Toast } from 'sharedUi/Toast';
import { RefreshButton } from 'sharedUi/RefreshButton';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Tag } from 'primereact/tag';
import { useDebounce } from '../../hooks/useDebounce';
import { extractErrorMessage } from '../../utils/error-handler';
import { partnerConnectionsService } from '../../services/partner-connections.service';
import { partnersService } from '../../services/partners.service';
import { connectionTypesService } from '../../services/connection-types.service';
import { projectsService, type AccessibleProject } from '../../services/projects.service';
import type { Partner, ConnectionType, PartnerConnection } from '../../types/partners';
import DeleteConfirmDialog from '../../components/dialogs/delete-confirm-dialog';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { useCanCreate, useCanUpdate, useCanDelete, useHasPermission } from '../../hooks/usePermissions';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import { JOB_NAMES } from '../../constants/jobNames';
import { PERMISSIONS } from '../../constants/permissions';
import PageHeader from '../../components/common/page-header';
import { ProjectCell } from '../../components/common/project-cell';
import SaasDataTable, {
  type SaasColumn, buildDefaultFilters, buildColumnFiltersParam,
} from '../../components/common/saas-data-table';

const FILTER_DEFS: SaasColumn[] = [
  { field: 'name', header: 'Name', filter: true },
  { field: 'authType', header: 'Auth', filter: true },
  { field: 'status', header: 'Status', filter: true },
];

const STATUS_SEVERITY: Record<string, 'success' | 'danger' | 'warning' | 'secondary'> = {
  ACTIVE: 'success', TEST_FAILED: 'danger', UNTESTED: 'warning', INACTIVE: 'secondary',
};

const DetailRow = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className="flex justify-between gap-4 border-b border-line pb-2">
    <span className="text-ink-muted">{label}</span>
    <span className="font-medium text-ink text-right">{value}</span>
  </div>
);

const PartnerConnectionsList = ({ embedded = false, appId }: { embedded?: boolean; appId?: string }) => {
  const navigate = useAppNavigate();
  const toast = useRef<Toast>(null);
  const canCreate = useCanCreate(JOB_NAMES.PARTNER_CONNECTIONS) && !appId;
  const canUpdate = useCanUpdate(JOB_NAMES.PARTNER_CONNECTIONS);
  const canDelete = useCanDelete(JOB_NAMES.PARTNER_CONNECTIONS);
  const canTest = useHasPermission(PERMISSIONS.PARTNER_CONNECTION_TEST);

  const [rows, setRows] = useState<PartnerConnection[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [types, setTypes] = useState<ConnectionType[]>([]);
  const [loading, setLoading] = useState(true);
  const includeDeleted = false; // "Show deleted" toggle removed; always hide deleted rows
  const [searchText, setSearchText] = useState('');
  const [accessibleProjects, setAccessibleProjects] = useState<AccessibleProject[]>([]);
  // Shown for ONE project (the Data Explorer): the screen opens already
  // narrowed to it, instead of to the session's project.
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(appId ?? null);
  // >1 accessible project ≈ platform/partner admin who may filter across projects.
  const canManageProjects = !appId && (accessibleProjects.length > 1);

  useEffect(() => {
    projectsService.getAccessible().then(setAccessibleProjects).catch(() => setAccessibleProjects([]));
  }, []);
  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<1 | -1 | null>(null);
  const [filters, setFilters] = useState<Record<string, any>>(() => buildDefaultFilters(FILTER_DEFS));

  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [viewRow, setViewRow] = useState<PartnerConnection | null>(null);

  const debouncedSearch = useDebounce(searchText, 500);
  const notify = (severity: 'success' | 'error' | 'warn', detail: string) =>
    toast.current?.show({ severity, summary: severity === 'success' ? 'Success' : severity === 'warn' ? 'Warning' : 'Error', detail, life: severity === 'error' ? 8000 : 4000 });

  useEffect(() => {
    (async () => {
      try {
        const [p, t] = await Promise.all([partnersService.getAllForDropdown(), connectionTypesService.getAllForDropdown()]);
        setPartners(p); setTypes(t);
      } catch { /* dropdowns optional */ }
    })();
  }, []);

  const partnerName = (id?: string) => partners.find((p) => p._id === id)?.name || id || '-';
  const typeCode = (id?: string) => types.find((t) => t._id === id)?.code || id || '-';
  // Auth type — admin-editable via the Value Sets screen, falling back to the
  // built-in list until the `connectionAuthType` set is seeded.

  const fetchRows = useCallback(async (page = 1, size = pageSize, sortBy = sortField, order = sortOrder, active = filters) => {
    try {
      setLoading(true);
      const params: any = { page, page_size: size };
      if (includeDeleted) params.include_deleted = true;
      if (debouncedSearch?.trim()) params.search = debouncedSearch.trim();
      if (sortBy) { params.sort_by = sortBy; params.sort_order = order === 1 ? 'asc' : 'desc'; }
      const fp = buildColumnFiltersParam(active);
      if (fp) params.filters = fp;
      if (selectedProjectId) params.app_id = selectedProjectId;
      const res = await partnerConnectionsService.getAll(params);
      setRows(res.data || []);
      setTotalRecords(res.pagination?.totalItems ?? (res.pagination as any)?.total ?? 0);
    } catch (err: any) {
      notify('error', extractErrorMessage(err, 'Failed to load connections.'));
    } finally {
      setLoading(false);
    }
  }, [includeDeleted, debouncedSearch, pageSize, sortField, sortOrder, filters, selectedProjectId]);

  useEffect(() => { setFirst(0); fetchRows(1, pageSize); }, [includeDeleted, debouncedSearch, selectedProjectId]); // eslint-disable-line

  const onPage = (e: any) => { setFirst(e.first); setPageSize(e.rows); fetchRows(Math.floor(e.first / e.rows) + 1, e.rows, sortField, sortOrder); };
  const onSort = (e: any) => { setSortField(e.sortField); setSortOrder(e.sortOrder); setFirst(0); fetchRows(1, pageSize, e.sortField, e.sortOrder); };
  const onFilter = (e: any) => { const next = e.filters || {}; setFilters(next); setFirst(0); fetchRows(1, pageSize, sortField, sortOrder, next); };
  const refresh = () => fetchRows(Math.floor(first / pageSize) + 1, pageSize, sortField, sortOrder);

  // Create and edit are a page now, like every other master. The form carries a
  // partner, a type, credentials, the type's declared settings and a JSON escape
  // hatch — more than a modal shows without scrolling itself.
  const openCreate = () => navigate('/partners/connections/create');
  const openEdit = (r: PartnerConnection) => navigate(`/partners/connections/${r._id}/edit`);

  const doTest = async (id: string) => {
    try {
      setTestingId(id);
      const res = await partnerConnectionsService.test(id);
      notify(res.ok ? 'success' : 'warn', `Test: ${res.status} — ${res.result?.message ?? ''}`);
      refresh();
    } catch (err: any) {
      notify('error', extractErrorMessage(err, 'Connection test failed'));
    } finally {
      setTestingId(null);
    }
  };

  const doDelete = async () => {
    if (!deleteId) return;
    try { setDeleteLoading(true); await partnerConnectionsService.delete(deleteId); setDeleteId(null); refresh(); }
    catch (err: any) { setDeleteId(null); notify('error', extractErrorMessage(err, 'Failed to delete connection')); }
    finally { setDeleteLoading(false); }
  };

  const columns: SaasColumn<PartnerConnection>[] = useMemo(() => {
    const cols: SaasColumn<PartnerConnection>[] = [
      { field: 'name', header: 'Name', sortable: true, filter: true, hideable: false },
      { field: 'partnerId', header: 'Partner', body: (r) => partnerName(r.partnerId) },
      { field: 'connectionType', header: 'Type', body: (r) => <span className="font-mono text-xs">{typeCode(r.connectionType)}</span> },
      { field: 'authType', header: 'Auth', filter: true, body: (r) => r.authType || '-' },
      { field: 'status', header: 'Status', filter: true, body: (r) => <Tag value={r.status || 'UNTESTED'} severity={STATUS_SEVERITY[r.status || 'UNTESTED'] || 'warning'} /> },
      { field: 'secrets', header: 'Secrets', align: 'center', body: (r) => (
        <span className="flex gap-1 justify-center text-xs">
          {r.hasAuthToken && <i className="pi pi-key text-amber-500" data-pr-tooltip="Token set" />}
          {r.hasPassword && <i className="pi pi-lock text-amber-500" data-pr-tooltip="Password set" />}
          {!r.hasAuthToken && !r.hasPassword && <span className="text-gray-400">—</span>}
        </span>
      ) },
    ];
    if (includeDeleted) cols.push({ field: 'del', header: 'Deleted', body: (r) => ((r as any).isDelete ? 'Yes' : 'No') });
    cols.push({
      field: 'appId', header: 'Project',
      body: (r) => <ProjectCell appId={r.appId} projects={accessibleProjects} />,
    });
    cols.push({
      field: 'actions', header: 'Actions', align: 'center', hideable: false,
      body: (r) => {
        const isDeleted = (r as any).isDelete;
        return (
          <div className="flex gap-1 justify-center">
            <button aria-label="View" className="p-2 rounded hover:bg-emerald-50 dark:hover:bg-emerald-900/20" onClick={() => setViewRow(r)} data-pr-tooltip="View"><i className="pi pi-eye text-emerald-600" style={{ fontSize: '1.05rem' }} /></button>
            {!isDeleted && canTest && (
              <button aria-label="Test" className="p-2 rounded hover:bg-indigo-50 dark:hover:bg-indigo-900/20" onClick={() => doTest(r._id)} disabled={testingId === r._id} data-pr-tooltip="Test">
                <i className={`pi ${testingId === r._id ? 'pi-spin pi-spinner' : 'pi-bolt'} text-indigo-600`} style={{ fontSize: '1.05rem' }} />
              </button>
            )}
            {!isDeleted && canUpdate && <button aria-label="Edit" className="p-2 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20" onClick={() => openEdit(r)} data-pr-tooltip="Edit"><i className="pi pi-pencil text-blue-600" style={{ fontSize: '1.05rem' }} /></button>}
            {!isDeleted && canDelete && <button aria-label="Delete" className="p-2 rounded hover:bg-red-50 dark:hover:bg-red-900/20" onClick={() => setDeleteId(r._id)} data-pr-tooltip="Delete"><i className="pi pi-trash text-red-600" style={{ fontSize: '1.05rem' }} /></button>}
          </div>
        );
      },
    });
    return cols;
  }, [canUpdate, canDelete, canTest, includeDeleted, testingId, partners, types, accessibleProjects]);

  return (
    <PermissionGuard jobName={JOB_NAMES.PARTNER_CONNECTIONS}>
      <Toast ref={toast} position="top-right" />
      <div className={embedded ? '' : 'p-3'} data-testid="SCR-PartnerConnections-List">
        {!embedded && (
          <PageHeader icon="pi pi-link" title="Partner Connections" subtitle="Endpoints & credentials to reach each partner" />
        )}
        <div className={embedded ? '' : 'bg-surface rounded-xl shadow-sm border border-line overflow-hidden'}>
          <SaasDataTable
            tableId="partner-connections" columns={appId ? columns.filter((c) => c.field !== 'appId') : columns} data={rows} loading={loading} dataKey="_id"
            emptyMessage="No connections found" first={first} rows={pageSize} totalRecords={totalRecords}
            onPage={onPage} rowsPerPageOptions={[5, 10, 25, 50]} sortField={sortField || undefined} sortOrder={sortOrder || undefined}
            onSort={onSort} filters={filters} onFilter={onFilter} globalSearch={searchText} onGlobalSearchChange={setSearchText}
            globalSearchPlaceholder="Search connections..." testId="PartnerConnectionsList"
            toolbarStart={
              <div className="flex items-center gap-3 flex-wrap">
                {canManageProjects && (
                  <Dropdown
                    value={selectedProjectId}
                    options={accessibleProjects.map((p) => ({ label: p.appCode ? `${p.name} (${p.appCode})` : p.name, value: p._id }))}
                    onChange={(e: any) => setSelectedProjectId(e.value || null)}
                    placeholder="Project"
                    showClear
                    style={{ minWidth: '160px' }}
                  />
                )}
              </div>
            }
            toolbarEnd={<><span className="text-sm text-ink-muted whitespace-nowrap">{totalRecords} {totalRecords === 1 ? 'record' : 'records'}</span><RefreshButton onClick={refresh} loading={loading} />{canCreate && <Button label="Create Connection" icon="pi pi-plus" size="small" onClick={openCreate} />}</>}
          />
        </div>

        <Dialog header="Connection Details" visible={!!viewRow} onHide={() => setViewRow(null)} style={{ width: '680px', maxWidth: '96vw' }}>
          {viewRow && (
            <div className="flex flex-col gap-3 pt-1 text-sm">
              <DetailRow label="Name" value={viewRow.name || '-'} />
              <DetailRow label="Partner" value={partnerName(viewRow.partnerId)} />
              <DetailRow label="Connection Type" value={<span className="font-mono">{typeCode(viewRow.connectionType)}</span>} />
              <DetailRow label="Auth Type" value={viewRow.authType || '-'} />
              <DetailRow label="Status" value={<Tag value={viewRow.status || 'UNTESTED'} severity={STATUS_SEVERITY[viewRow.status || 'UNTESTED'] || 'warning'} />} />
              {viewRow.connectionLoginUser && <DetailRow label="Login User" value={viewRow.connectionLoginUser} />}
              <DetailRow label="Secrets" value={
                <span className="flex gap-2 justify-end">
                  <span className={viewRow.hasAuthToken ? 'text-amber-600' : 'text-gray-400'}><i className="pi pi-key mr-1" />Token {viewRow.hasAuthToken ? 'set' : 'none'}</span>
                  <span className={viewRow.hasPassword ? 'text-amber-600' : 'text-gray-400'}><i className="pi pi-lock mr-1" />Password {viewRow.hasPassword ? 'set' : 'none'}</span>
                </span>
              } />
              {viewRow.sessionTimeoutPeriod != null && <DetailRow label="Session Timeout (ms)" value={viewRow.sessionTimeoutPeriod} />}
              <DetailRow label="Active" value={viewRow.isActive ? 'Yes' : 'No'} />
              {viewRow.comments && <DetailRow label="Comments" value={viewRow.comments} />}
              {viewRow.connectionConfig && (
                <div className="flex flex-col gap-1">
                  <span className="text-ink-muted">Connection Config</span>
                  <pre className="bg-canvas dark:bg-gray-900 rounded p-2 text-xs overflow-auto max-h-64 font-mono">{JSON.stringify(viewRow.connectionConfig, null, 2)}</pre>
                </div>
              )}
              <p className="mt-1 text-xs text-ink-muted"><i className="pi pi-lock mr-1" /> Secret values are never displayed.</p>
            </div>
          )}
        </Dialog>

        <DeleteConfirmDialog visible={!!deleteId} onCancel={() => setDeleteId(null)} onDelete={doDelete} message="Delete this connection? It will be marked as deleted and its secrets removed." loading={deleteLoading} />
      </div>
    </PermissionGuard>
  );
};

export default PartnerConnectionsList;
